import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  Search,
  Bell,
  User,
  Plus,
  Star,
  Crown,
  Menu,
  X,
  ArrowLeft,
  LogOut,
} from "lucide-react";

interface HeaderProps {
  sidebarOpen: boolean;
  toggleSidebar: () => void;
}

const Header: React.FC<HeaderProps> = ({
  sidebarOpen,
  toggleSidebar,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const firstName = user?.given_name || user?.name || "User";
  const profileImage = user?.picture || "";

  const handleBack = () => {
    navigate(-1);
  };

  const handleLogout = useCallback(() => {
    localStorage.clear();
    navigate("/home");
  }, [navigate]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const showBackButton =
    location.pathname !== "/home" && location.pathname !== "/";

  return (
    <header
      className="w-full h-20 px-6 flex items-center justify-between shadow-sm"
      style={{
        backgroundColor: "rgb(var(--color-bg-white))",
        borderBottom: "1px solid rgb(var(--color-border))",
      }}
    >
      {/* Left Section */}
      <div className="flex items-center space-x-4">
        {/* Sidebar Toggle */}
        <button
          onClick={toggleSidebar}
          className="p-2 sqsft-btn-ghost rounded-lg transition-all duration-200"
          title={sidebarOpen ? "Close sidebar" : "Open sidebar"}
        >
          {sidebarOpen ? (
            <X className="w-5 h-5" />
          ) : (
            <Menu className="w-5 h-5" />
          )}
        </button>

        {/* Back Button */}
        {showBackButton && (
          <button
            onClick={handleBack}
            className="p-2 sqsft-btn-ghost rounded-lg transition-all duration-200"
            title="Go back"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
        )}

        {/* Search Section */}
        <div className="relative max-w-md w-full">
          <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
            <Search
              className="w-5 h-5"
              style={{ color: "rgb(var(--color-text-muted))" }}
            />
          </div>
          <input
            type="text"
            placeholder="Search innovations..."
            className="sqsft-input rounded-xl pl-10"
          />
        </div>
      </div>

      {/* Right Section */}
      <div className="flex items-center space-x-4">
        {/* Quick Actions */}
        <Link
          to="/roadmap-innovation"
          className="hidden md:flex items-center px-4 py-2 sqsft-btn-primary text-sm rounded-lg transition-all duration-200 shadow-sm hover:shadow-md"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Roadmap
        </Link>

        {/* Notifications */}
        <button className="relative p-2 sqsft-btn-ghost rounded-lg transition-all duration-200">
          <Bell className="w-5 h-5" />
          <span className="absolute -top-1 -right-1 w-5 h-5 sqsft-gradient-gold text-white text-xs rounded-full flex items-center justify-center shadow-lg">
            2
          </span>
        </button>

        {/* User Profile with Dropdown */}
        <div
          className="flex items-center space-x-3 pl-3 border-l relative"
          style={{ borderColor: "rgb(var(--color-border))" }}
          ref={dropdownRef}
        >
          <div
            className="relative cursor-pointer"
            onClick={() => setShowDropdown((prev) => !prev)}
          >
            <div className="w-10 h-10 rounded-xl flex items-center justify-center shadow-lg">
              { profileImage ? 
                <img src={profileImage} alt="Profile" className="w-full h-full rounded-full" />
                : <User className="w-5 h-5 text-white" />
              }
            </div>
            <div className="absolute -bottom-1 -right-1 w-4 h-4 sqsft-gradient-gold rounded-full flex items-center justify-center shadow-md">
              <Crown className="w-2.5 h-2.5 text-white" />
            </div>
          </div>

          <div className="hidden md:block">
            <div className="flex items-center space-x-2">
              <p className="text-sm sqsft-heading-4">{firstName}</p>
              <div className="flex items-center px-2 py-1 sqsft-badge sqsft-badge-gold rounded-full">
                <Star
                  className="w-3 h-3 mr-1"
                  style={{ color: "rgb(var(--color-royal-gold))" }}
                />
                <span
                  className="text-xs font-medium"
                  style={{ color: "rgb(var(--color-royal-gold))" }}
                >
                  Pro
                </span>
              </div>
            </div>
            <p className="text-xs sqsft-caption"> Product Manager</p>
          </div>

          {/* Dropdown */}
          {showDropdown && (
            <div className="absolute top-14 right-0 w-40 bg-white rounded-lg shadow-md z-50 p-2">
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-2 px-3 py-2 rounded-md hover:bg-gray-100 text-sm text-left"
              >
                <LogOut className="w-4 h-4" />
                Logout
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
